import { AfterViewInit, Component, HostListener, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID, SimpleChanges } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from "@amcharts/amcharts5/percent";
import { isPlatformBrowser } from '@angular/common';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import { TranslateService } from '@ngx-translate/core';
import { TranslateDataKeysService } from 'src/shared/services/translate-data-keys.service';

@Component({
  selector: 'atom-pie-chart',
  templateUrl: './pie-chart.component.html',
  styleUrls: ['./pie-chart.component.scss'],
})
export class PieChartComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root | undefined;
  private chart!: am5percent.PieChart;

  @Input() chartData: Record<string, string | number >[] = []; // Datos a graficar (debe ser un array de objetos JSON)
  @Input() dataLabel: string = ''; // Nombre del campo en los datos que se usará para las etiquetas de cada categoría en las rebanadas (slice) del gráfico
  @Input() dataValue: string = '';  // Nombre del campo en los datos que se usará para los valores de cada rebanada (slice) del gráfico
  @Input() chartId: string = 'chart-' + Math.random().toString(36).substr(2, 9); // Genera un id único para renderizar el gráfico
  @Input() fileName: string = ''; // Nombre del archivo al exportar
  @Input() language:string = ""; // Idioma de la aplicación
  @Input() colors?: string[] = []; // Colores para cada rebanada (slice) del gráfico
  @Input() showValueInTooltip?: boolean = false; // Controla si el valor debe mostrarse en el tooltip del gráfico

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object, // Inyecta el identificador de la plataforma (navegador o servidor)
    private zone: NgZone,  // Inyecta NgZone para manejar la detección de cambios y ejecutar código fuera del contexto de Angular
    private translateService: TranslateService, private translateDataKeysService: TranslateDataKeysService,
  ) {}

  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) { // Verifica si la aplicación se está ejecutando en el navegador
      this.zone.runOutsideAngular(() => {  // Ejecuta el código fuera del contexto de Angular para evitar la detección de cambios innecesaria
        f(); // Llama a la función proporcionada como parametro
      });
    }
  }

  ngOnInit(): void {}

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if (this.root) {
      this.updateLabels(); // Actualiza la leyenda del gráfico cuando cambia el tamaño de la ventana
    }
  }

  // Método llamado después de que la vista del componente se haya inicializado
  ngAfterViewInit() {
    this.browserOnly(() => {   // Ejecuta el código solo en el navegador
      this.root = am5.Root.new(this.chartId);  // Crea una nueva instancia de 'am5.Root' asociada al elemento con id 'pie-chart' (contenedor donde se construirá el gráfico)
      this.renderChart();  // Llama al método para configurar y renderizar el gráfico
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.root && (changes['chartData'] || changes['language'])){
      this.root.dispose();
      this.root = undefined;

      // Vuelve a renderizar el gráfico
      this.browserOnly(() => {
        this.root = am5.Root.new(this.chartId);
        this.renderChart();
      });
    }
  }

  ngOnDestroy() {
    this.browserOnly(() => {   // Ejecuta el código solo en el navegador
      if (this.root) {   // Verifica si 'this.root' está definido
        this.root.dispose(); // Libera los recursos asociados a la instancia de 'am5.Root' para evitar fugas de memoria
      }
    });
  }

  private applyCustomChartTheme () {
    if(!this.root) return;

    // Crea un nuevo tema utilizando la instancia raíz del gráfico (this.root)
    // Este tema personalizado (myTheme) se puede usar para configurar estilos específicos en el gráfico
    const myTheme = am5.Theme.new(this.root);

    // Aplica los temas: animado, personalizado y responsive
    this.root.setThemes([
      am5themes_Animated.new(this.root),
      myTheme,
      am5themes_Responsive.new(this.root)
    ]);
  }

  private renderChart() {
    if (!this.root) return; // Si 'this.root' no esta definido, detiene la ejecución del método

    // Ordenar la data de forma descendente según el campo de valor
    const sortedData = [...this.chartData].sort((a, b) => {
      const aValue = Number(a[this.dataValue]);
      const bValue = Number(b[this.dataValue]);
      return bValue - aValue;
    });

    this.applyCustomChartTheme(); // Llama al método para establecer los temas personalizados al gráfico

    // Crea y agrega un gráfico percent - PieChart al contenedor de la raíz del gráfico
    this.chart = this.root.container.children.push(
      am5percent.PieChart.new(this.root, {
        layout: this.root.verticalLayout // Establece que los elementos deben apilarse verticalmente (uno debajo del otro), el gráfico se colocará siguiendo esa disposición vertical
      })
    );

    // Agrega una nueva serie al gráfico
    let series = this.chart.series.push(
      am5percent.PieSeries.new(this.root, {
        valueField: this.dataValue,   // Campo de datos que representa los valores de cada rebanada (slice) del gráfico
        categoryField: this.dataLabel, // Campo de datos que se usa para etiquetar cada rebanada (slice) del gráfico, representando las categorías correspondientes
      })
    );

    // Configuración de la serie
    series.setAll({
      radius: am5.percent(90)
    });

    series.labels.template.setAll({
      text: "{category}: {valuePercentTotal.formatNumber('0.00')}%",
      maxWidth: 250,
      oversizedBehavior: "wrap",
      textAlign: "center",
      forceHidden: false,
      paddingTop: 8,
      paddingBottom: 8
    });

    // Establece el tooltip para cada rebanada (slice) del gráfico
    if(this.showValueInTooltip) {
      series.slices.template.set("tooltipText", "{category}: {valuePercentTotal.formatNumber('0.00')}%[/] ({value})");
    } else {
      series.slices.template.set("tooltipText", "{category}: {valuePercentTotal.formatNumber('0.00')}%[/]");
    }

    // Asigna los colores dinámicamente si hay colores proporcionados a cada rebanada (slice) del gráfico
    if (this.colors && this.colors.length > 0) {
      series.get("colors")?.set("colors", this.colors.map(color => am5.color(color)));
    }

    // Personalizar el borde (stroke) de la serie
    series.slices.template.setAll({
      stroke: am5.color(0xffffff), // Color del borde de la línea
      strokeWidth: 2 // Ancho del borde de la línea
    });

    this.updateLabels();

    series.data.setAll(sortedData); // Establece todos los datos de la serie usando los datos ordenados
    series.appear(2000); // Hace que la serie aparezca en el gráfico con una animación que dura 2000 ms (2 segundos)
    this.chart.appear(2000, 100); // Hace que el gráfico aparezca con una animación que dura 2000 ms (2 segundos) y un retraso de 100 ms (100 milisegundos)

    // Agrega las opciones para la exportación
    let exporting = am5plugins_exporting.Exporting.new(this.root, {
      filePrefix: `${this.fileName}`, // Nombre de los archivos exportados
      menu: am5plugins_exporting.ExportingMenu.new(this.root, {}), // Añade un menú de opciones de exportación en el gráfico
      dataSource: this.translateDataKeysService.translateKeys(sortedData, 'CHARTS'), // Especifica los datos que se exportarán, usando 'chartData' como fuente
      htmlOptions: {
        disabled: true, // Desactiva la exportación en formato HTML
      },
      jsonOptions: {
        disabled: true, // Desactiva la exportación en formato JSON
      }
    });
  }

  private updateLabels() {
    if (!this.root) return;

    // Define si las etiquetas y ticks deben ser visibles o no
    const shouldShowLabels = window.innerWidth >= 992;

    // Aplica configuraciones globales para todas las series
    this.chart.series.each((series) => {
      series.labels.template.setAll({
        visible: shouldShowLabels
      });
      series.ticks.template.setAll({
        visible: shouldShowLabels
      });
    });
  }

  private updateChartData() {
    if (this.chart) {
      this.chart.series.each((series) => { // Actualiza los datos de todas las series del gráfico. Cada vez que se cambian los datos (cuando se actualiza this.chartData)
        series.data.setAll(this.chartData);
      });
    }
  }
}
